import React, { useState } from "react";
import CustomSelect from "components/custom-select/CustomSelect";
import Checkbox from "components/checkbox/Checkbox";

import { Button, Form, Row, Col } from "react-bootstrap";
import IconButton from "components/icon-button";
import ConfirmModal from "components/confirm-modal";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import {
  PSYCHOLOGIST_LICENSE_TYPE,
  SOCIAL_WORKER_LICENSE_TYPE,
  THERAPIST_LICENSE_TYPE,
  PSYCHOLOGIST_LICENSE_TYPE_UNDER_SUPERVISION,
  SOCIAL_WORKER_LICENSE_TYPE_UNDER_SUPERVISION,
  THERAPIST_LICENSE_TYPE_UNDER_SUPERVISION,
  MEMBER_BOARD_OPTIONS_PHYSICIAN,
  MEMBER_BOARD_OPTIONS_PHYSICIAN_FLATTEN,
  MEMBER_BOARD_OPTIONS_PSYCHOLOGIST,
  MEMBER_BOARD_OPTIONS_SOCIAL_WORKER,
  MEMBER_BOARD_OPTIONS_THERAPIST,
  SPECIALTY_BY_MEMBER_BOARD,
  SUBSPECIALITY_BY_MEMBER_BOARD,
  YEAR_OPTIONS,
  LICENSE_EXPIRATION_YEAR_OPTIONS,
  LICENSE_EXPIRATION_MONTH_OPTIONS,
} from "../utility/constants";
import { STATE_OPTIONS } from "constants/stateOptions";
import {
  getItemsByOrchidProType,
  getItemsNotCurrOrchidProType,
} from "../utility/getItemsByOrchidProType";
import {
  checkIsPhysician,
  checkIsPsychologist,
  checkIsSocialWorker,
} from "../utility/checkOrchidProType";
import getOrchidProTypeLabel from "../utility/getOrchidProTypeLabel";
import messages from "messages";
import getClasses from "utils/getClasses";
import styles from "./ProCredentials.module.scss";
import getOrchidProTypeOrder from "../utility/getOrchidProTypeOrder";
import { track } from "utils/amplitude";

const UNDER_SUPERVISION = [
  { label: "I'm not under supervision.", value: "under supervision" },
  {
    label: "I'm under supervision.",
    value: "not under supervision",
  },
];

const getLicenseTypeOptions = (orchidProType, isUnderSupervision) => {
  switch (orchidProType.toLowerCase()) {
    case "psychologist": {
      if (isUnderSupervision) {
        return PSYCHOLOGIST_LICENSE_TYPE_UNDER_SUPERVISION;
      } else {
        return PSYCHOLOGIST_LICENSE_TYPE;
      }
    }
    case "social worker": {
      if (isUnderSupervision) {
        return SOCIAL_WORKER_LICENSE_TYPE_UNDER_SUPERVISION;
      } else {
        return SOCIAL_WORKER_LICENSE_TYPE;
      }
    }
    case "therapist": {
      if (isUnderSupervision) {
        return THERAPIST_LICENSE_TYPE_UNDER_SUPERVISION;
      } else {
        return THERAPIST_LICENSE_TYPE;
      }
    }
  }
};

const getFinalLicensesArr = (licensesArr, orchidProTypeId) => {
  return licensesArr.length === 0
    ? [
        {
          type: null,
          conferring_entity: null,
          license_number: null,
          expiration_date: null,
          supervision: {
            has_supervision: false,
            name: null,
            npi: null,
            phone: null,
            license_type: null,
            license_number: null,
          },
          orchid_pro_type_id: orchidProTypeId,
        },
      ]
    : licensesArr;
};

const getFinalCertificationsArr = (certificationsArr, orchidProTypeId) => {
  return certificationsArr.length === 0
    ? [
        {
          orchid_pro_type_id: orchidProTypeId,
          year: null,
          board: null,
          specialty: null,
          sub_specialty: null,
        },
      ]
    : certificationsArr;
};

// const getFinalQualificationsArr = (qualificationsArr, orchidProTypeId) => {
//   return qualificationsArr.length === 0
//     ? [
//         {
//           certificate_name: null,
//           certificate_identifier: null,
//           conferring_entity: null,
//           conferring_entity_type: null,
//           year: null,
//           orchid_pro_type_id: orchidProTypeId,
//         },
//       ]
//     : qualificationsArr;
// };

const ProCredentials = ({
  data,
  registration,
  fieldChangedCallback,
  amplitudePageName,
}) => {
  const [licenseIndexToDelete, setLicenseIndexToDelete] = useState(undefined);
  const [certificateIndexToDelete, setCertificateIndexToDelete] =
    useState(undefined);

  const setPsypact = (orchidProTypeId) => (e) => {
    const licenses = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const psypact = {
      type: null,
      conferring_entity: "PSYPACT",
      license_number: null,
      expiration_date: null,
      supervision: {
        has_supervision: false,
        name: null,
        npi: null,
        phone: null,
        license_type: null,
        license_number: null,
      },
      orchid_pro_type_id: orchidProTypeId,
    };
    const nextLicenses = e.currentTarget.checked
      ? remained.concat(licenses.concat(psypact))
      : remained.concat(
          licenses.filter((license) => license.conferring_entity !== "PSYPACT"),
        );
    fieldChangedCallback("licenses", nextLicenses);
  };

  const addLicense = (orchidProTypeId) => {
    const licensesArr = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const licenses = getFinalLicensesArr(licensesArr, orchidProTypeId);
    const newLicense = {
      type: null,
      conferring_entity: null,
      license_number: null,
      expiration_date: null,
      supervision: {
        has_supervision: false,
        name: null,
        npi: null,
        phone: null,
        license_type: null,
        license_number: null,
      },
      orchid_pro_type_id: orchidProTypeId,
    };
    fieldChangedCallback(
      "licenses",
      remained.concat(licenses.concat(newLicense)),
    );
  };

  const removeLicense = (orchidProTypeId) => {
    const licenses = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    licenses.splice(licenseIndexToDelete, 1);

    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    fieldChangedCallback("licenses", remained.concat(licenses));
  };

  const removeCertification = (orchidProTypeId) => {
    const certifications = getItemsByOrchidProType(
      data.board_certifications,
      orchidProTypeId,
    );
    certifications.splice(certificateIndexToDelete, 1);
    const remained = getItemsNotCurrOrchidProType(
      data.board_certifications,
      orchidProTypeId,
    );
    fieldChangedCallback(
      "board_certifications",
      remained.concat(certifications),
    );
  };

  const addSupervision = (orchidProTypeId) => {
    // filter out PSYPACT!
    const licensesArr = getItemsByOrchidProType(
      data.licenses,
      orchidProTypeId,
    ).filter((license) => license.conferring_entity !== "PSYPACT");

    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const licenses = getFinalLicensesArr(licensesArr, orchidProTypeId);

    licenses[0] = {
      ...licenses[0],
      supervision: {
        has_supervision: true,
        name: null,
        npi: null,
        license_type: null,
        license_number: null,
        phone: null,
      },
    };
    fieldChangedCallback("licenses", remained.concat([licenses[0]]));
  };

  const removeSupervision = (orchidProTypeId) => {
    const licenses = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    // check if remained arr is changed
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const nextLicenses = licenses.slice(0);
    nextLicenses.forEach(
      (license) =>
        (license.supervision = {
          has_supervision: false,
          name: null,
          npi: null,
          license_type: null,
          license_number: null,
          phone: null,
        }),
    );
    fieldChangedCallback("licenses", remained.concat(nextLicenses));
  };

  const onSupervisorInputChange = (orchidProTypeId) => (event) => {
    // get licenses arr for currnet orchid pro type
    const licenses = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const inputField = event.currentTarget;
    const { name, value } = inputField;
    const fieldName = name === "supervisor_name" ? "name" : name;
    const { supervision } = licenses[0];

    licenses[0] = {
      ...licenses[0],
      supervision: { ...supervision, [fieldName]: value },
    };

    fieldChangedCallback("licenses", remained.concat(licenses));
  };

  const onSupervisorSelectChange = (orchidProTypeId) => (event) => {
    const licenses = getItemsByOrchidProType(data.licenses, orchidProTypeId);
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const { supervision } = licenses[0];

    licenses[0] = {
      ...licenses[0],
      supervision: { ...supervision, license_type: event.value },
    };
    fieldChangedCallback("licenses", remained.concat(licenses));
  };

  // const onInputChange = (event) => {
  //   const inputField = event.currentTarget;
  //   fieldChangedCallback(inputField.name, inputField.value);
  // };

  const onLicenseSelectChange = (fieldName, orchidProTypeId, index) => (e) => {
    const licensesArr = getItemsByOrchidProType(
      data.licenses,
      orchidProTypeId,
    ).filter((license) => license.conferring_entity !== "PSYPACT");
    const psypact = getItemsByOrchidProType(
      data.licenses,
      orchidProTypeId,
    ).filter((license) => license.conferring_entity === "PSYPACT");

    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );
    const licenses = getFinalLicensesArr(licensesArr, orchidProTypeId);

    const { expiration_date } = licenses[index];
    let currExpirationMonth = "";
    let currExpirationYear = "";
    if (expiration_date) {
      if (expiration_date.length === 10 && expiration_date.startsWith("xxxx")) {
        currExpirationMonth = expiration_date.substring(5, 7);
      } else if (
        expiration_date.length === 10 &&
        !expiration_date.startsWith("xxxx") &&
        expiration_date.includes("xx")
      ) {
        currExpirationYear = expiration_date.substring(0, 4);
      } else {
        currExpirationMonth = expiration_date.substring(5, 7);
        currExpirationYear = expiration_date.substring(0, 4);
      }
    }

    if (fieldName === "expiration_year") {
      licenses[index] = {
        ...licenses[index],
        expiration_date: `${e.value}-${
          currExpirationMonth ? currExpirationMonth : "xx"
        }-01`,
      };
    } else if (fieldName === "expiration_month") {
      licenses[index] = {
        ...licenses[index],
        expiration_date: `${currExpirationYear ? currExpirationYear : "xxxx"}-${
          e.value
        }-01`,
      };
    } else {
      licenses[index] = {
        ...licenses[index],
        [fieldName]: e.value,
      };
    }

    fieldChangedCallback("licenses", psypact.concat(remained.concat(licenses)));
  };

  const onLicenseInputChange = (orchidProTypeId, index) => (event) => {
    const licensesArr = getItemsByOrchidProType(
      data.licenses,
      orchidProTypeId,
    ).filter((license) => license.conferring_entity !== "PSYPACT");
    const psypact = getItemsByOrchidProType(
      data.licenses,
      orchidProTypeId,
    ).filter((license) => license.conferring_entity === "PSYPACT");
    const remained = getItemsNotCurrOrchidProType(
      data.licenses,
      orchidProTypeId,
    );

    const licenses = getFinalLicensesArr(licensesArr, orchidProTypeId);
    const inputField = event.currentTarget;

    licenses[index] = {
      ...licenses[index],
      [inputField.name]: inputField.value,
    };
    fieldChangedCallback("licenses", psypact.concat(remained.concat(licenses)));
  };

  const onBoardCertificationSelectChange =
    (index, fieldName, orchidProTypeId) => (e) => {
      const boardCertificationsArr = getItemsByOrchidProType(
        data.board_certifications,
        orchidProTypeId,
      );
      const remained = getItemsNotCurrOrchidProType(
        data.board_certifications,
        orchidProTypeId,
      );
      const boardCertifications = getFinalCertificationsArr(
        boardCertificationsArr,
        orchidProTypeId,
      );
      boardCertifications[index] = {
        ...boardCertifications[index],
        [fieldName]: e.value,
      };
      fieldChangedCallback(
        "board_certifications",
        remained.concat(boardCertifications),
      );
    };

  const addBoardCertification = (orchidProTypeId) => {
    const boardCertificationsArr = getItemsByOrchidProType(
      data.board_certifications,
      orchidProTypeId,
    );
    const remained = getItemsNotCurrOrchidProType(
      data.board_certifications,
      orchidProTypeId,
    );
    const boardCertifications = getFinalCertificationsArr(
      boardCertificationsArr,
      orchidProTypeId,
    );
    const newBoardCertification = {
      orchid_pro_type_id: orchidProTypeId,
      year: null,
      board: null,
      specialty: null,
      sub_specialty: null,
    };
    fieldChangedCallback(
      "board_certifications",
      remained.concat(boardCertifications.concat(newBoardCertification)),
    );
  };

  return !data.orchid_pro_types?.length ||
    data.orchid_pro_types?.length === 0 ? null : (
    <div className={!registration ? styles["intake-form--edit-profile"] : ""}>
      {data.orchid_pro_types.map((item, index) => {
        const isPhysician = checkIsPhysician(item);
        const isPsychologist = checkIsPsychologist(item);
        const isSocialWorker = checkIsSocialWorker(item);

        const isPsypact = getItemsByOrchidProType(
          data.licenses,
          item.orchid_pro_type_id,
        )?.some((license) => license.conferring_entity === "PSYPACT");

        const licensesArr = getItemsByOrchidProType(
          data.licenses,
          item.orchid_pro_type_id,
        )?.filter((license) => license.conferring_entity !== "PSYPACT");

        const licenses = getFinalLicensesArr(
          licensesArr,
          item.orchid_pro_type_id,
        );
        const isUnderSupervision = licenses.some(
          (license) => license.supervision?.has_supervision,
        );
        const boardArr = getItemsByOrchidProType(
          data.board_certifications,
          item.orchid_pro_type_id,
        );
        const board = getFinalCertificationsArr(
          boardArr,
          item.orchid_pro_type_id,
        );
        // const qualificationsArr = getItemsByOrchidProType(
        //   data.qualifications,
        //   item.orchid_pro_type_id,
        // );
        // const qualification = getFinalCertificationsArr(
        //   qualificationsArr,
        //   item.orchid_pro_type_id,
        // );

        const MEMBER_BOARD_OPTIONS_NON_PHYSICIAN = isPsychologist
          ? MEMBER_BOARD_OPTIONS_PSYCHOLOGIST
          : isSocialWorker
          ? MEMBER_BOARD_OPTIONS_SOCIAL_WORKER
          : MEMBER_BOARD_OPTIONS_THERAPIST;

        return (
          <div
            className={styles["intake-form__pro-type-block"]}
            key={item.orchid_pro_type_id || index}
          >
            <Col md={6} lg={6} xs={12} className={"px-0"}>
              <div className={styles["intake-form__i-am-a--profile"]}>
                {getOrchidProTypeOrder(index)}-
                {getOrchidProTypeLabel(item.orchid_pro_type.type)}
              </div>
            </Col>
            <div className={styles["edit-profile__card"]}>
              <div
                className={getClasses(
                  styles["intake-form__title"],
                  styles["intake-form__title--edit-profile"],
                )}
              >
                License Details
              </div>
              {!isPhysician && (
                <>
                  <div className={styles["intake-form__title"]}>
                    Are you under supervision?
                  </div>

                  <div className={styles["intake-form__radio-group"]}>
                    <CustomSelectionControl
                      type={"radio"}
                      checked={!isUnderSupervision}
                      id={UNDER_SUPERVISION[0].value}
                      name={`supervision-${item.orchid_pro_type_id}`}
                      value={UNDER_SUPERVISION[0].value}
                      onChange={() => {
                        track(
                          amplitudePageName,
                          "are-you-under-supervision-radio-button",
                          "click",
                        );
                        removeSupervision(item.orchid_pro_type_id);
                      }}
                    />
                    <label
                      htmlFor={UNDER_SUPERVISION[0].value}
                      className={styles["intake-form__radio-label"]}
                    >
                      {UNDER_SUPERVISION[0].label}
                    </label>
                  </div>

                  <div className={styles["intake-form__radio-group"]}>
                    <CustomSelectionControl
                      type={"radio"}
                      checked={isUnderSupervision}
                      id={UNDER_SUPERVISION[1].value}
                      name={`supervision-${item.orchid_pro_type_id}`}
                      value={UNDER_SUPERVISION[1].value}
                      onChange={() => {
                        track(
                          amplitudePageName,
                          "are-you-under-supervision-radio-button",
                          "click",
                        );
                        addSupervision(item.orchid_pro_type_id);
                      }}
                    />
                    <label
                      htmlFor={UNDER_SUPERVISION[1].value}
                      className={styles["intake-form__radio-label"]}
                    >
                      {UNDER_SUPERVISION[1].label}
                    </label>
                  </div>
                </>
              )}

              {/* confirm delete license */}
              <ConfirmModal
                labelTitle={messages.areYouSure}
                children={messages.doYouWantToDeleteThisSection}
                labelConfirm={messages.delete}
                hideCancel={true}
                show={licenseIndexToDelete !== undefined}
                onCancel={() => setLicenseIndexToDelete(undefined)}
                onConfirm={() => {
                  removeLicense(item.orchid_pro_type_id);
                  setLicenseIndexToDelete(undefined);
                }}
              />

              <Row>
                {licenses.map((license, index) => {
                  const licenseExpirationYear = parseInt(
                    license.expiration_date?.substring(0, 4),
                  );
                  const licenseExpirationMonth =
                    license.expiration_date?.substring(5, 7);
                  return (
                    <Col
                      key={index}
                      md={6}
                      lg={6}
                      xs={12}
                      className={styles["pro-credentials__col--profile"]}
                    >
                      <div
                        className={getClasses(
                          "d-flex justify-content-between",
                          licenses.length > 1 && index >= 0 && "mb-3",
                        )}
                      >
                        {licenses.length > 1 && (
                          <p className={styles["pro-credentials__text"]}>
                            License {index + 1}
                          </p>
                        )}
                        {!!index && (
                          <IconButton
                            variant="link"
                            onClick={() => {
                              setLicenseIndexToDelete(index);
                              track(
                                amplitudePageName,
                                "license-delete-button",
                                "click",
                              );
                            }}
                          >
                            <OutlineCross className="gray02" />
                          </IconButton>
                        )}
                      </div>
                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          State{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <CustomSelect
                          required={index === 0}
                          isMulti={false}
                          value={
                            STATE_OPTIONS.find(
                              (option) =>
                                option.value === license.conferring_entity,
                            ) || ""
                          }
                          onChange={onLicenseSelectChange(
                            "conferring_entity",
                            item.orchid_pro_type_id,
                            index,
                          )}
                          options={STATE_OPTIONS}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "license-state-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>

                      {!isPhysician && (
                        <Form.Group>
                          <Form.Label
                            className={styles["intake-form__form-label"]}
                          >
                            License Type{" "}
                            {index === 0 && <span className="required">*</span>}
                          </Form.Label>
                          {(() => {
                            const OPTIONS = getLicenseTypeOptions(
                              item.orchid_pro_type?.type,
                              isUnderSupervision,
                            );
                            const CURR_VALUE = OPTIONS?.find(
                              (option) => option.value === license.type,
                            );
                            return (
                              <CustomSelect
                                // required={index === 0}
                                isMulti={false}
                                onChange={onLicenseSelectChange(
                                  "type",
                                  item.orchid_pro_type_id,
                                  index,
                                )}
                                value={CURR_VALUE || ""}
                                options={OPTIONS}
                                key={`license_type_${item.orchid_pro_type?.type}_${index}`}
                                onClick={() =>
                                  track(
                                    amplitudePageName,
                                    "license-type-dropdown",
                                    "click",
                                  )
                                }
                              />
                            );
                          })()}
                        </Form.Group>
                      )}

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          License Number{" "}
                          {index === 0 && <span className="required">*</span>}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          required={index === 0}
                          value={license.license_number || ""}
                          name="license_number"
                          onChange={onLicenseInputChange(
                            item.orchid_pro_type_id,
                            index,
                          )}
                          placeholder={"Enter license number"}
                          onFocus={() =>
                            track(
                              amplitudePageName,
                              "license-number-field",
                              "focus",
                            )
                          }
                        />
                      </Form.Group>
                      <Form.Label className={styles["intake-form__form-label"]}>
                        Expiration Date{" "}
                        {index === 0 && <span className="required">*</span>}
                      </Form.Label>
                      <Row>
                        <Form.Group as={Col}>
                          <CustomSelect
                            required={licenseExpirationYear || index === 0}
                            isMulti={false}
                            value={
                              LICENSE_EXPIRATION_MONTH_OPTIONS.find(
                                (option) =>
                                  option.value === licenseExpirationMonth,
                              ) || ""
                            }
                            placeholder={"Month"}
                            onChange={onLicenseSelectChange(
                              "expiration_month",
                              item.orchid_pro_type_id,
                              index,
                            )}
                            options={LICENSE_EXPIRATION_MONTH_OPTIONS}
                            onClick={() =>
                              track(
                                amplitudePageName,
                                "license-expiration-month",
                                "click",
                              )
                            }
                          />
                        </Form.Group>
                        <Form.Group as={Col}>
                          <CustomSelect
                            required={licenseExpirationMonth || index === 0}
                            isMulti={false}
                            value={
                              LICENSE_EXPIRATION_YEAR_OPTIONS.find(
                                (option) =>
                                  option.value === licenseExpirationYear,
                              ) || ""
                            }
                            placeholder={"Year"}
                            onChange={onLicenseSelectChange(
                              "expiration_year",
                              item.orchid_pro_type_id,
                              index,
                            )}
                            options={LICENSE_EXPIRATION_YEAR_OPTIONS}
                            onClick={() =>
                              track(
                                amplitudePageName,
                                "license-expiration-year",
                                "click",
                              )
                            }
                          />
                        </Form.Group>
                      </Row>

                      {isUnderSupervision && (
                        <>
                          <hr />
                          <div className={styles["intake-form__title"]}>
                            Provide Supervisor's Details
                          </div>
                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Supervisor Name{" "}
                              <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                              required={isUnderSupervision}
                              type="text"
                              value={licenses[0].supervision.name || ""}
                              name="supervisor_name"
                              onChange={onSupervisorInputChange(
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Supervisor Name"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "supervisor-name-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Supervisor's NPI{" "}
                              <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                              required={isUnderSupervision}
                              maxLength={10}
                              minLength={10}
                              type="text"
                              value={licenses[0].supervision.npi || ""}
                              onChange={onSupervisorInputChange(
                                item.orchid_pro_type_id,
                              )}
                              name="npi"
                              placeholder={"Enter Supervisor NPI"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "supervisor-npi-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              License Type
                            </Form.Label>
                            {(() => {
                              const OPTIONS = getLicenseTypeOptions(
                                item.orchid_pro_type?.type,
                                isUnderSupervision,
                              );
                              const CURR_VALUE = OPTIONS?.find(
                                (option) =>
                                  option.value ===
                                  licenses[0].supervision.license_type,
                              );
                              return (
                                <CustomSelect
                                  isMulti={false}
                                  onChange={onSupervisorSelectChange(
                                    item.orchid_pro_type_id,
                                  )}
                                  placeholder={"Select License Type"}
                                  value={CURR_VALUE || ""}
                                  options={OPTIONS}
                                  key={`license_type_${item.orchid_pro_type?.type}_${index}`}
                                  onClick={() =>
                                    track(
                                      amplitudePageName,
                                      "license-type-dropdown",
                                      "click",
                                    )
                                  }
                                />
                              );
                            })()}
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              License Number
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="license_number"
                              value={
                                licenses[0].supervision.license_number || ""
                              }
                              onChange={onSupervisorInputChange(
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter License Number"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "license-number-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>

                          <Form.Group>
                            <Form.Label
                              className={styles["intake-form__form-label"]}
                            >
                              Supervisor Contact Number{" "}
                              <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                              required={isUnderSupervision}
                              type="text"
                              name="phone"
                              value={licenses[0].supervision.phone || ""}
                              onChange={onSupervisorInputChange(
                                item.orchid_pro_type_id,
                              )}
                              placeholder={"Enter Supervisor Number"}
                              onFocus={() =>
                                track(
                                  amplitudePageName,
                                  "supervisor-contact-number-field",
                                  "focus",
                                )
                              }
                            />
                          </Form.Group>
                        </>
                      )}
                    </Col>
                  );
                })}
              </Row>

              {!isUnderSupervision && (
                <Button
                  variant="link"
                  className="d-flex align-items-center mb-3 p-0"
                  onClick={() => {
                    track(amplitudePageName, "add-license-button", "click");
                    addLicense(item.orchid_pro_type_id);
                  }}
                >
                  <OutlinePlus16 className="mr-1" /> Add License Type
                </Button>
              )}

              <Col md={6} lg={6} xs={12} className={"px-0"}>
                {isPsychologist && !isUnderSupervision && (
                  <>
                    <div
                      className={getClasses(
                        styles["intake-form__title"],
                        styles["intake-form__extra-spacing"],
                      )}
                    >
                      PSYPACT
                    </div>
                    <Checkbox
                      label={
                        <label className="rg-14 mb-3">
                          I have the Authority to Practice Interjurisdictional
                          Telepsychology (APIT) under PSYPACT
                        </label>
                      }
                      id={`PSYPACT_${item.orchid_pro_type_id}`}
                      onChange={setPsypact(item.orchid_pro_type_id)}
                      checked={isPsypact}
                      onClick={() =>
                        track(amplitudePageName, "psypact-checkbox", "click")
                      }
                    />
                  </>
                )}
              </Col>
            </div>

            <div className={styles["edit-profile__card"]}>
              <div
                className={getClasses(
                  styles["intake-form__title"],
                  styles["intake-form__title--edit-profile"],
                )}
              >
                Board Certification
              </div>
              {/* confirm delete board certification */}
              <ConfirmModal
                labelTitle={messages.areYouSure}
                children={messages.doYouWantToDeleteThisSection}
                labelConfirm={messages.delete}
                hideCancel={true}
                show={certificateIndexToDelete !== undefined}
                onCancel={() => setCertificateIndexToDelete(undefined)}
                onConfirm={() => {
                  removeCertification(item.orchid_pro_type_id);
                  setCertificateIndexToDelete(undefined);
                }}
              />

              <Row>
                {board.map((boardCertification, index) => {
                  return (
                    <Col
                      key={index}
                      md={6}
                      lg={6}
                      xs={12}
                      className={styles["pro-credentials__col--profile"]}
                    >
                      <div
                        className={getClasses(
                          "d-flex justify-content-between",
                          board.length > 1 && index >= 0 && "mb-3",
                        )}
                      >
                        {board.length > 1 && (
                          <p className={styles["pro-credentials__text"]}>
                            Board Certification {index + 1}
                          </p>
                        )}
                        {!!index && (
                          <IconButton
                            variant="link"
                            onClick={() => setCertificateIndexToDelete(index)}
                          >
                            <OutlineCross className="gray02" />
                          </IconButton>
                        )}
                      </div>

                      <Form.Group>
                        <Form.Label
                          className={styles["intake-form__form-label"]}
                        >
                          Year
                        </Form.Label>
                        <CustomSelect
                          isMulti={false}
                          value={
                            YEAR_OPTIONS.find(
                              (option) =>
                                option.value === boardCertification.year,
                            ) || ""
                          }
                          placeholder={"Select Graduation Year"}
                          onChange={onBoardCertificationSelectChange(
                            index,
                            "year",
                            item.orchid_pro_type_id,
                          )}
                          options={YEAR_OPTIONS}
                          onClick={() =>
                            track(
                              amplitudePageName,
                              "board-certification-year-dropdown",
                              "click",
                            )
                          }
                        />
                      </Form.Group>
                      {(() => {
                        const SPECIALTY_OPTIONS =
                          SPECIALTY_BY_MEMBER_BOARD.filter(
                            (specialty) =>
                              specialty.board === boardCertification.board,
                          );

                        const SUBSPECIALTY_OPTIONS =
                          SUBSPECIALITY_BY_MEMBER_BOARD.filter(
                            (subspecialty) =>
                              subspecialty.board === boardCertification.board &&
                              subspecialty.specialty ===
                                boardCertification.specialty,
                          );

                        return (
                          <>
                            <Form.Group>
                              <Form.Label
                                className={styles["intake-form__form-label"]}
                              >
                                Member Board
                              </Form.Label>
                              <CustomSelect
                                isMulti={false}
                                value={
                                  (!isPhysician
                                    ? MEMBER_BOARD_OPTIONS_NON_PHYSICIAN.find(
                                        (option) =>
                                          option.value ===
                                          boardCertification.board,
                                      )
                                    : MEMBER_BOARD_OPTIONS_PHYSICIAN_FLATTEN.find(
                                        (option) =>
                                          option.value ===
                                          boardCertification.board,
                                      )) || ""
                                }
                                onChange={onBoardCertificationSelectChange(
                                  index,
                                  "board",
                                  item.orchid_pro_type_id,
                                )}
                                options={
                                  !isPhysician
                                    ? MEMBER_BOARD_OPTIONS_NON_PHYSICIAN
                                    : MEMBER_BOARD_OPTIONS_PHYSICIAN
                                }
                                placeholder={"Select Member Board"}
                                key={`board-${item.orchid_pro_type_id}-${index}`}
                                onClick={() =>
                                  track(
                                    amplitudePageName,
                                    "member-board-dropdown",
                                    "click",
                                  )
                                }
                              />
                            </Form.Group>
                            {isPhysician && (
                              <>
                                <Form.Group>
                                  <Form.Label
                                    className={
                                      styles["intake-form__form-label"]
                                    }
                                  >
                                    Specialty
                                  </Form.Label>
                                  <CustomSelect
                                    isMulti={false}
                                    value={
                                      SPECIALTY_OPTIONS.find(
                                        (option) =>
                                          option.value ===
                                          boardCertification.specialty,
                                      ) || ""
                                    }
                                    onChange={onBoardCertificationSelectChange(
                                      index,
                                      "specialty",
                                      item.orchid_pro_type_id,
                                    )}
                                    options={SPECIALTY_OPTIONS}
                                    placeholder={"Select Specialty"}
                                    key={`specialty_${item.orchid_pro_type_id}-${boardCertification.board}`}
                                    onClick={() =>
                                      track(
                                        amplitudePageName,
                                        "specialty-dropdown",
                                        "click",
                                      )
                                    }
                                  />
                                </Form.Group>
                                <Form.Group>
                                  <Form.Label
                                    className={
                                      styles["intake-form__form-label"]
                                    }
                                  >
                                    Sub-Specialty
                                  </Form.Label>
                                  <CustomSelect
                                    isMulti={false}
                                    value={
                                      SUBSPECIALTY_OPTIONS.find(
                                        (option) =>
                                          option.value ===
                                          boardCertification.sub_specialty,
                                      ) || ""
                                    }
                                    onChange={onBoardCertificationSelectChange(
                                      index,
                                      "sub_specialty",
                                      item.orchid_pro_type_id,
                                    )}
                                    options={SUBSPECIALTY_OPTIONS}
                                    placeholder={"Select Sub-Specialty"}
                                    key={`subspecialty_${item.orchid_pro_type_id}-${boardCertification.board}-${boardCertification.specialty}`}
                                    onClick={() =>
                                      track(
                                        amplitudePageName,
                                        "subspecialty-dropdown",
                                        "click",
                                      )
                                    }
                                  />
                                </Form.Group>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </Col>
                  );
                })}
              </Row>
              <Button
                variant="link"
                className="mb-3 d-flex align-items-center p-0"
                onClick={() => {
                  track(
                    amplitudePageName,
                    "add-board-certification-button",
                    "click",
                  );
                  addBoardCertification(item.orchid_pro_type_id);
                }}
              >
                <OutlinePlus16 className="mr-1" /> Add Board Certification
              </Button>
              {/* <hr className={styles["edit-profile__hr"]} />
              <Col
                md={6}
                lg={6}
                xs={12}
                className={getClasses(
                  "pl-0",
                  styles["pro-credentials__col--profile"],
                  styles["edit-profile__col"],
                )}
              >
                <div
                  className={getClasses(
                    styles["intake-form__title"],
                    styles["intake-form__extra-spacing"],
                    styles["intake-form__title--edit-profile"],
                  )}
                >
                  Additional Certification
                </div>
                <Form.Group>
                  <Form.Label className={styles["intake-form__form-label"]}>
                    Orchid Certification
                  </Form.Label>
                  <CustomSelect
                    isMulti={false}
                    value={
                      (!isPhysician
                        ? MEMBER_BOARD_OPTIONS_NON_PHYSICIAN.find(
                            (option) =>
                              option.value ===
                              qualification.certificate_identifier,
                          )
                        : MEMBER_BOARD_OPTIONS_PHYSICIAN_FLATTEN.find(
                            (option) =>
                              option.value ===
                              qualification.certificate_identifier,
                          )) || ""
                    }
                    // onChange={onOtherCertificationSelectChange(
                    //   index,
                    //   "certificate_identifier",
                    //   item.orchid_pro_type_id,
                    // )}
                    options={
                      !isPhysician
                        ? MEMBER_BOARD_OPTIONS_NON_PHYSICIAN
                        : MEMBER_BOARD_OPTIONS_PHYSICIAN
                    }
                    placeholder={"Select Certification"}
                    key={`qualification-${item.orchid_pro_type_id}-${index}`}
                  />
                </Form.Group>
              </Col> */}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProCredentials;
